import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Code, Zap, Bot, Cpu, Shield, ChevronUp, Send, Calendar, Phone } from 'lucide-react';
import emailjs from '@emailjs/browser';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Footer from '../components/Footer';
import ScrollIndicator from '../components/ScrollIndicator';

const Services = () => {
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [formData, setFormData] = useState({
    from_name: '',
    reply_to: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setShowScrollTop(scrollTop > 800);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Update the form's message field to include contact details
    if (formRef.current) {
      const messageInput = formRef.current.querySelector('textarea[name="message"]') as HTMLTextAreaElement;
      const contactDetails = `Name: ${formData.from_name}\nEmail: ${formData.reply_to}\nPhone: ${formData.phone}\n\nMessage:\n${formData.message}`;
      messageInput.value = contactDetails;
    }

    try {
      const result = await emailjs.sendForm(
        'service_l7n36yv',
        'template_pcoocgf',
        formRef.current!,
        '3pGgUTaif-_dIInLH'
      );

      if (result.text === 'OK') {
        toast.success('Message sent successfully!');
        setFormData({
          from_name: '',
          reply_to: '',
          phone: '',
          subject: '',
          message: ''
        });
      }
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
      console.error('Error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="h-screen overflow-y-scroll snap-y snap-mandatory scroll-container">
      <ToastContainer position="bottom-right" />
      
      {/* Hero Section */}
      <section className="relative bg-slate-900 text-white min-h-[90vh] snap-start flex flex-col justify-center">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-slate-900/0"></div>
        <div className="container-width relative flex-1 flex flex-col justify-center items-center py-16 sm:py-20">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white to-blue-200">Our Services</h1>
            <div className="space-y-4">
              <p className="text-base sm:text-lg text-slate-300">
Still wasting time on things a machine could do faster (and without complaining)? Whether you're a trader chasing the markets or a business buried in endless manual work, we build smart automation that does the heavy lifting—so you can focus on what actually moves the needle.
              </p>
            </div>
          </div>
        </div>
        <ScrollIndicator text="Explore Our Services" />
      </section>

      {/* Combined ViSiON & ViSFLOW Section */}
      <section className="min-h-screen snap-start bg-slate-50 flex items-center py-24">
        <div className="container-width">
          {/* ViSiON */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden p-8 sm:p-10 mb-16">
            <div className="flex items-center mb-8">
              <Zap className="w-12 h-12 text-blue-500 mr-4" />
              <h2 className="text-3xl font-bold">ViSiON – Algorithmic Trading & Backtesting</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <ServiceCard
                icon={<Code />}
                title="Custom Trading Algorithms"
                features={[
                  "We code high-performance, rule-based trading strategies so you can stop guessing and start executing.",
                  "Optimized for speed, because milliseconds matter in trading (unless you like donating money to the market).",
                  "Fully customizable—whether you're scalping, swinging, or doing something only you understand."
                ]}
              />

              <ServiceCard
                icon={<Shield />}
                title="Backtesting & Strategy Validation"
                features={[
                  "Test strategies against historical data before trusting them with real money (highly recommended).",
                  "Find inefficiencies before they drain your account.",
                  "Get performance reports packed with key trading metrics—so you know what’s working and what’s wishful thinking."
                ]}
              />

              <ServiceCard
                icon={<Cpu />}
                title="Trading Infrastructure & Automation"
                features={[
                  "API integration with brokers and exchanges—because manual trading is so last decade.",
                  "Automated execution designed for low-latency trading (a.k.a. fast enough to matter).",
                  "Risk management and order optimization, because blowing up your account isn’t a strategy."
                ]}
              />
            </div>
          </div>
          
          {/* ViSFLOW */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden p-8 sm:p-10">
            <div className="flex items-center mb-8">
              <Bot className="w-12 h-12 text-blue-500 mr-4" />
              <h2 className="text-3xl font-bold">ViSFLOW – Process Automation & Chatbots</h2>
              
            </div>
 
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <ServiceCard
                icon={<Code />}
                title="Python Automation Solutions"
                features={[
      "Custom scripts to wipe out manual tasks—because clicking buttons all day isn’t a career goal.",
      "Data processing, web scraping, and system automation that actually works.",
      "We build custom APIs using Flask, FastAPI, and more—because sometimes you need the internet to actually listen to you."
                ]}
              />

              <ServiceCard
                icon={<Bot />}
                title="Telegram & Discord Chatbots"
                features={[
      "AI-driven bots that handle customer support, so you don’t have to.",
      "Community management tools with smart automated responses—perfect for when people ask the same question 500 times.",
      "Seamless API/database integration for bots that actually do things, not just sit there."
                ]}
              />

              <ServiceCard
                icon={<Cpu />}
                title="Game Bots & Interactive Automation"
                features={[
      "Discord game bots that keep communities engaged while you sleep.",
      "Automated in-game functionalities so you can focus on winning, not grinding.",
      "Custom-built features for specific gaming communities—because one-size-fits-all is just lazy."
                ]}
              />
            </div>
          </div>
        </div>
      </section> 
 
      {/* CTA & Footer Section */}
      <section className="snap-start">
        <div className="bg-white py-24">
          <div className="container-width">
            <div className="bg-blue-500 text-white rounded-xl p-8 sm:p-10">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
                {/* CTA Content */}
                <div className="text-center md:text-left">
                  <h2 className="text-3xl font-bold mb-4">Umm so what do you say?</h2>
                  <p className="text-xl mb-8">Got an idea, a problem, or just tired of doing things manually? Let’s talk.</p>
                  <div className="space-y-4">
                    <p className="text-lg">Email: <a href="mailto:contact@visrupt.in" className="underline hover:text-blue-100">contact@visrupt.in</a></p>
                    <p className="text-lg">Phone: <a href="tel:+917522979568" className="underline hover:text-blue-100">+91-7522979568</a></p>
                  </div>
                  <a
                    href="https://calendly.com/visrupt/schedule-a-call"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-6 py-3 bg-white text-blue-500 rounded-xl font-semibold mt-6 hover:bg-blue-50 transition-all duration-300 group"
                  >
                    <Calendar className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform duration-300" />
                    <span className="group-hover:translate-x-1 transition-transform duration-300">Schedule a Call</span>
                  </a>
                </div>

                {/* Contact Form */}
                <div className="md:col-span-2">
                  <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 sm:p-8 border border-white/10">
                    <h3 className="text-xl font-semibold mb-6">Send us a message</h3>
                    <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
                      <div>
                        <label htmlFor="from_name" className="block text-sm font-medium text-white/90 mb-1">
                          Name
                        </label>
                        <input
                          type="text"
                          id="from_name"
                          name="from_name"
                          value={formData.from_name}
                          onChange={handleChange}
                          className="w-full rounded-lg bg-white/10 border-white/20 text-white placeholder-white/60"
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="reply_to" className="block text-sm font-medium text-white/90 mb-1">
                          Email
                        </label>
                        <input
                          type="email"
                          id="reply_to"
                          name="reply_to"
                          value={formData.reply_to}
                          onChange={handleChange}
                          className="w-full rounded-lg bg-white/10 border-white/20 text-white placeholder-white/60"
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-white/90 mb-1">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className="w-full rounded-lg bg-white/10 border-white/20 text-white placeholder-white/60"
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="subject" className="block text-sm font-medium text-white/90 mb-1">
                          Subject
                        </label>
                        <input
                          type="text"
                          id="subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          className="w-full rounded-lg bg-white/10 border-white/20 text-white placeholder-white/60"
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="message" className="block text-sm font-medium text-white/90 mb-1">
                          Message
                        </label>
                        <textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          rows={4}
                          className="w-full rounded-lg bg-white/10 border-white/20 text-white placeholder-white/60"
                          required
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-white text-blue-500 px-6 py-3 rounded-lg font-semibold hover:bg-blue-50
                                 transition transform hover:scale-[1.02] flex items-center justify-center group
                                 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Send className={`w-5 h-5 mr-2 ${isSubmitting ? 'animate-pulse' : 'group-hover:translate-x-1 transition-transform duration-300'}`} />
                        {isSubmitting ? 'Sending...' : 'Send Message'}
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Footer />
      </section>

      {/* Scroll to Top Button */}
      <button
        onClick={scrollToTop}
        className={`fixed bottom-8 right-8 bg-blue-500 text-white p-3 rounded-full shadow-lg transition-all duration-300 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 ${
          showScrollTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-16'
        }`}
        aria-label="Scroll to top"
      >
        <ChevronUp className="w-6 h-6" />
      </button>
    </div>
  );
};

const ServiceCard = ({ icon, title, features }) => (
  <div className="bg-slate-50 rounded-lg p-6 sm:p-8">
    <div className="text-blue-500 mb-4">
      {React.cloneElement(icon, { className: 'w-8 h-8' })}
    </div>
    <h3 className="text-xl font-semibold mb-4">{title}</h3>
    <ul className="space-y-3">
      {features.map((feature, index) => (
        <li key={index} className="flex items-start">
          <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0" />
          <span className="text-slate-600">{feature}</span>
        </li>
      ))}
    </ul>
  </div>
);

export default Services;